import { Component, Inject, OnInit, inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { MasterService } from "@frontend/app/serivces/master.service";
import { SVG_ICONS } from "@frontend/app/models/icons";
import { SanitizerPipe } from "@frontend/app/pipes/sanitizer.pipe";
import { FormsModule } from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatTableModule } from "@angular/material/table";
import { MatPaginatorModule, PageEvent } from "@angular/material/paginator";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";

interface StudentEnrollment {
  studentId: number;
  studentName: string;
  className: string;
  classCode: string;
  startTime: string;
  endTime: string;
  isPresent: boolean;
  hasTodayAttendance: boolean;
  // Keep original student data for display
  originalStudent: any;
}
@Component({
  selector: "app-manual-attendance",
  standalone: true,
  imports: [CommonModule, SanitizerPipe, FormsModule, MatFormFieldModule, MatInputModule, MatIconModule, MatButtonModule, MatTableModule, MatPaginatorModule],
  templateUrl: "./manual-attendance.component.html",
  styleUrls: ["./manual-attendance.component.scss"],
})
export class ManualAttendanceComponent implements OnInit {
  private masterService = inject(MasterService);
  private dialogRef = inject(MatDialogRef<ManualAttendanceComponent>);

  constructor(@Inject(MAT_DIALOG_DATA) public data: any) {}

  ngOnInit(): void {
    if (this.data.studentList) {
      // Store the student IDs to be excluded
      const excludedStudentIds = this.data.studentList.map((s: any) => s.StudentID);
      // Pass the excluded IDs to loadStudents
      this.loadStudents(excludedStudentIds);
    } else {
      this.loadStudents([]);
    }
  }
  // Table data
  displayedColumns: string[] = ["FirstName", "CenterName", "ClassName", "attendance"];
  dataSource: any[] = [];
  filteredStudents: any[] = [];
  searchTerm = "";

  // Pagination
  pageSize = 10;
  pageIndex = 0;
  totalItems = 0;
  pageSizeOptions = [5, 10, 25, 50];
  students: any;

  // Loading state
  isLoading = false;

  readonly SVG_ICONS = SVG_ICONS;
  activeEnrollments: StudentEnrollment[] = [];

  loadStudents(excludedStudentIds: number[] = []) {
    this.isLoading = true;
    this.masterService.getStudentList(1, 10000, "asc", "", "", "", "").subscribe({
      next: (res: any) => {
        // Filter out students who are in the excluded list or already have attendanceStatus as 'Present'
        this.dataSource = (res.Table || []).filter((student: any) => !excludedStudentIds.includes(student.StudentID));

        // Process to get active enrollments
        this.activeEnrollments = this.dataSource.flatMap(student =>
          (student.classEnrollments || [])
            .filter((enrollment: any) => enrollment.IsActive)
            .map((enrollment: any) => ({
              studentId: student.StudentID,
              studentName: `${student.FirstName} ${student.MiddleName} ${student.LastName}`.trim(),
              className: enrollment.classDetails?.ClassName || "",
              classCode: enrollment.classDetails?.ClassCode || "",
              startTime: enrollment.classDetails?.StartTime,
              endTime: enrollment.classDetails?.EndTime,
              centerName: student.center?.CenterName || "",
              isPresent: false,
              originalStudent: student, // Keep reference to original student data
            }))
        );

        this.filteredStudents = [...this.activeEnrollments];

        this.totalItems = this.filteredStudents.length;
        this.loadTodayAttendance();
        this.applyPagination();
        this.isLoading = false;
      },
      error: error => {
        console.error("Error loading students:", error);
        this.isLoading = false;
      },
    });
  }

  loadTodayAttendance() {
    const todayDate = new Date().toISOString().split("T")[0];

    this.masterService.getStudentAttendence(this.data.ClassID, todayDate).subscribe((res: any) => {
      const attendanceRecords = res || [];

      this.activeEnrollments = this.activeEnrollments.map(enrollment => {
        const student = enrollment.originalStudent;

        const record = attendanceRecords.find((a: any) => a.StudentID === student.StudentID);

        if (record) {
          enrollment.isPresent = record.AttendanceStatus === "Present";

          // Parse Last3DaysAttendance safely
          let last3Days = [];
          try {
            last3Days = JSON.parse(record.Last3DaysAttendance || "[]");
          } catch {
            last3Days = [];
          }

          const hasTodayAttendance = last3Days.some((a: any) => a.AttendanceDate === todayDate);

          enrollment.hasTodayAttendance = hasTodayAttendance;
        } else {
          enrollment.isPresent = false;
          enrollment.hasTodayAttendance = false;
        }

        return enrollment;
      });

      // Refresh UI pagination
      this.applyFilter();
    });
  }

  applyFilter() {
    if (!this.searchTerm.trim()) {
      this.filteredStudents = [...this.activeEnrollments];
    } else {
      const searchLower = this.searchTerm.toLowerCase();
      this.filteredStudents = this.activeEnrollments.filter(
        enrollment =>
          enrollment.studentName.toLowerCase().includes(searchLower) ||
          enrollment.className.toLowerCase().includes(searchLower) ||
          enrollment.originalStudent.MobileNo?.includes(this.searchTerm) ||
          (enrollment.originalStudent.Email?.toLowerCase() || "").includes(searchLower)
      );
    }
    this.totalItems = this.filteredStudents.length;
    this.pageIndex = 0;
    this.applyPagination();
  }

  toggleAttendance(enrollment: StudentEnrollment) {
    enrollment.isPresent = !enrollment.isPresent;

    const status = enrollment.isPresent ? "Present" : "Absent";
    this.saveAttendance(enrollment, status);
  }

  saveAttendance(enrollment: StudentEnrollment, status: "Present" | "Absent") {
    const student = enrollment.originalStudent;

    const classEnroll = student.classEnrollments?.[0];
    if (!classEnroll) {
      console.error("No class enrollment found for student", student);
      return;
    }

    const attendanceDate = new Date().toISOString().split("T")[0];

    // If Absent -> delete attendance
    if (status === "Absent") {
      this.masterService.deleteAttendance(student.StudentID, classEnroll.ClassID, attendanceDate).subscribe({
        next: () => {},
        error: err => console.error("Error removing attendance", err),
      });
      return;
    }

    // If Present -> add attendance
    const attendanceRecord = {
      StudentID: Number(student.StudentID),
      CenterID: Number(student.CenterID),
      TrainerID: Number(classEnroll.TrainerID || 0),
      ClassID: Number(classEnroll.ClassID),
      AttendanceDate: new Date().toISOString(),
      SessionStart: new Date().toISOString(),
      SessionEnd: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(), // +2 hrs
      ModeOfEntry: "Manual",
      AttendanceStatus: status,
    };

    const attendanceData = { records: [attendanceRecord] };

    this.masterService.addStudentAttendance(attendanceData).subscribe({
      next: res => {},
      error: err => console.error("Error adding attendance", err),
    });
  }

  applyPagination() {
    const startIndex = this.pageIndex * this.pageSize;
    this.filteredStudents = this.filteredStudents.slice(startIndex, startIndex + this.pageSize);
  }

  // Add this method to your component class
  getInitials(name: string): string {
    if (!name) return "??";
    return name
      .split(" ")
      .map(n => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  }

  onPageChange(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.applyPagination();
  }

  markAttendance(student: any, status: "Present" | "Absent") {
    // Implement attendance marking logic here
    // Call your attendance service here
  }
  getStudentProfileImage(student: any): string {
    // 1. Priority: Check if the student has ProfileDocuments with an image
    if (student.ProfileDocuments && student.ProfileDocuments.length > 0) {
      const img = student.ProfileDocuments.find((d: any) => d.FileType === "IMAGE" && d.URL);
      if (img) {
        return img.URL; // Already full URL from your API
      }
    }

    // 2. Check for ProfilePicture (backward compatibility)
    if (student.ProfilePicture) {
      return student.ProfilePicture;
    }

    // 3. Check for Documents (alternative location)
    if (student.Documents && student.Documents.length > 0) {
      const img = student.Documents.find((d: any) => d.FileType === "IMAGE" && d.URL);
      if (img) {
        return img.URL;
      }
    }

    // 4. Fallback to local default image
    return "public/no-image.jpg";
  }

  closeDialog() {
    this.dialogRef.close();
  }
}
